import { addAssortmentItemSchema, AddAssortmentItemSchema } from '@retail/products/components';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ItemPriority, Supplier } from '@retail/products/types';
import { useAppTFunction } from '@retail/app/i18n';
import { DialogActions, DialogContent, Stack } from '@mui/material';
import { ContainedButton, OutlinedButton, SelectInputField } from '@shared/components';
import { InputDatePicker } from '@retail/components';
import { useMemo } from 'react';
import { StockedStatusToggleGroup, SupplierPriorityToggleGroup } from './AddAssortmentItem';

export type AddToAssortmentFunction = ({
  selectedSupplierId,
  fromDate,
  toDate,
  priority,
  isStocked,
}: {
  selectedSupplierId: number;
  fromDate: Date;
  toDate?: Date;
  priority: ItemPriority;
  isStocked: boolean;
}) => void;

interface Props {
  availableSuppliers?: Supplier[];
  supplierId?: number;
  includeStocked?: boolean;
  handleAddItem: AddToAssortmentFunction;
  onClose: () => void;
}
export function AddItemToAssortmentDialogContent({
  availableSuppliers,
  supplierId,
  includeStocked = true,
  handleAddItem,
  onClose,
}: Props) {
  const t = useAppTFunction();

  const { control, handleSubmit, formState } = useForm<AddAssortmentItemSchema>({
    resolver: yupResolver(addAssortmentItemSchema(t)),
    mode: 'onSubmit',
    defaultValues: {
      selectedSupplierId: availableSuppliers ? undefined : supplierId,
      priority: ItemPriority.ZERO,
      isStocked: true,
      fromDate: new Date(),
    },
  });

  const supplierIdController = useController({ control, name: 'selectedSupplierId' });
  const fromDateController = useController({ control, name: 'fromDate' });
  const toDateController = useController({ control, name: 'toDate' });
  const isStockedController = useController({ control, name: 'isStocked' });
  const priorityController = useController({ control, name: 'priority' });

  const onSubmit = handleSubmit(({ selectedSupplierId, fromDate, toDate, priority, isStocked }) => {
    handleAddItem({
      selectedSupplierId: selectedSupplierId ?? supplierId,
      fromDate,
      toDate: toDate ?? undefined,
      priority,
      isStocked,
    });
  });

  const supplierOptions = useMemo(
    () =>
      availableSuppliers?.map((item) => {
        return {
          value: item.id,
          name: item.name,
        };
      }),
    [availableSuppliers]
  );

  return (
    <>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {supplierOptions && (
          <SelectInputField
            {...supplierIdController.field}
            size="small"
            value={supplierIdController.field.value ?? ''}
            menuItems={supplierOptions}
            label={t('products.supplier.name')}
            placeholder={t('products.mgItem.documentation.selectSupplier')}
          />
        )}
        <Stack flexDirection="row" gap={6}>
          <SupplierPriorityToggleGroup
            selectedPriority={priorityController.field.value}
            setPriority={(prio: ItemPriority) => priorityController.field.onChange(prio)}
          />
          {includeStocked && (
            <StockedStatusToggleGroup
              isStocked={isStockedController.field.value}
              setIsStocked={(stocked: boolean) => isStockedController.field.onChange(stocked)}
            />
          )}
        </Stack>
        <Stack flexDirection="row" gap={2}>
          <InputDatePicker
            {...fromDateController.field}
            error={fromDateController.fieldState.error?.message}
            label={t('products.mgItem.fromDate.label')}
            minDate={new Date()}
            maxDate={toDateController.field.value ?? undefined}
            margin="none"
            size="small"
          />
          <InputDatePicker
            {...toDateController.field}
            error={toDateController.fieldState.error?.message}
            label={t('products.mgItem.toDate.label')}
            minDate={fromDateController.field.value ?? new Date()}
            margin="none"
            size="small"
            slotProps={{ field: { clearable: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <OutlinedButton size="small" onClick={onClose}>
          {t('products.cancel')}
        </OutlinedButton>
        <ContainedButton size="small" disabled={!formState.isValid} onClick={onSubmit}>
          {t('products.mgItem.addToMy')}
        </ContainedButton>
      </DialogActions>
    </>
  );
}
